import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ElementRef,
  type HTMLAttributes,
} from 'react';
import * as RD from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '../../lib/cn';

/**
 * Dialog primitive (Compose API over Radix UI).
 *
 * Provides Esc-to-close, focus trap and focus restoration to the trigger out
 * of the box. The visual structure follows DESIGN seção 9: Portal + Overlay
 * + centered Content with a built-in close button (top-right).
 */
export const Dialog = RD.Root;
export const DialogTrigger = RD.Trigger;
export const DialogClose = RD.Close;
export const DialogPortal = RD.Portal;

export const DialogOverlay = forwardRef<
  ElementRef<typeof RD.Overlay>,
  ComponentPropsWithoutRef<typeof RD.Overlay>
>(function DialogOverlay({ className, ...props }, ref) {
  return (
    <RD.Overlay
      ref={ref}
      className={cn(
        'fixed inset-0 z-40 bg-black/60 backdrop-blur-sm animate-fade-in',
        className,
      )}
      {...props}
    />
  );
});

export interface DialogContentProps extends ComponentPropsWithoutRef<typeof RD.Content> {
  /** Show the built-in close button on the top-right corner. Defaults to `true`. */
  showCloseButton?: boolean;
}

export const DialogContent = forwardRef<ElementRef<typeof RD.Content>, DialogContentProps>(
  function DialogContent({ className, children, showCloseButton = true, ...props }, ref) {
    return (
      <DialogPortal>
        <DialogOverlay />
        <RD.Content
          ref={ref}
          className={cn(
            'fixed left-1/2 top-1/2 z-50 flex w-full max-w-lg -translate-x-1/2 -translate-y-1/2 ' +
              'max-h-[90vh] flex-col rounded-card border border-border bg-bg-card p-6 shadow-2xl ' +
              'animate-slide-up focus:outline-none',
            className,
          )}
          {...props}
        >
          {children}
          {showCloseButton ? (
            <RD.Close
              className={cn(
                'absolute right-4 top-4 rounded-md p-1 text-muted transition-colors hover:text-white ' +
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ' +
                  'focus-visible:ring-offset-2 focus-visible:ring-offset-bg-card',
              )}
              aria-label="Fechar"
            >
              <X size={18} aria-hidden />
            </RD.Close>
          ) : null}
        </RD.Content>
      </DialogPortal>
    );
  },
);

export function DialogHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('flex flex-col gap-1.5 pr-8', className)} {...props} />;
}

export function DialogFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
      {...props}
    />
  );
}

export const DialogTitle = forwardRef<
  ElementRef<typeof RD.Title>,
  ComponentPropsWithoutRef<typeof RD.Title>
>(function DialogTitle({ className, ...props }, ref) {
  return <RD.Title ref={ref} className={cn('text-lg font-semibold text-white', className)} {...props} />;
});

export const DialogDescription = forwardRef<
  ElementRef<typeof RD.Description>,
  ComponentPropsWithoutRef<typeof RD.Description>
>(function DialogDescription({ className, ...props }, ref) {
  return <RD.Description ref={ref} className={cn('text-sm text-muted', className)} {...props} />;
});
